import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Skeleton,
  Alert,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import PatrolStatsCard from './components/PatrolStatsCard';
import CheckpointStatsCard from './components/CheckpointStatsCard';
import PatrolTable from './components/PatrolTable';
import SearchFilters from './components/SearchFilters';
import DurationStatsPanel from './components/DurationStatsPanel';

import { useActivityReport } from 'hooks/activityReport/useActivityReport';
import { useCustomerSearch, useAddressSearch } from 'hooks/activityReport/useCustomerAddressSearch';
import { useActivityReportInsight } from 'hooks/activityReport/useActivityReportInsight';

import { AIInsightPanel } from 'components/emailHistory/AIInsightPanel';
import { AIInsightSkeleton } from 'components/emailHistory/AIInsightSkeleton';
import { AIInsightError } from 'components/emailHistory/AIInsightError';

const ActivityReport = () => {
  const { userInfo } = useSelector(selectUserInfo);

  const isClientRole = useMemo(() => {
    return userInfo?.userRoleGroups?.some(
      (role) => role.name === 'CANdispatch_Client'
    ) || false;
  }, [userInfo?.userRoleGroups]);

  const clientCustomerId = isClientRole ? userInfo?.customerId : null;

  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [customer, setCustomer] = useState(null);
  const [address, setAddress] = useState(null);

  const [appliedParams, setAppliedParams] = useState({
    customerId: null,
    addressId: null,
    startDate: null,
    endDate: null,
  });

  const { customers, isLoading: customersLoading } = useCustomerSearch(clientCustomerId);

  useEffect(() => {
    if (isClientRole && customers.length > 0 && !customer) {
      setCustomer(customers[0]);
    }
  }, [isClientRole, customers, customer]);

  const { addresses, isLoading: addressesLoading } = useAddressSearch(customer?.Id || null);

  const {
    patrols,
    patrolStats,
    checkpointStats,
    durationStats,
    isLoading: reportLoading,
    error: reportError,
    refetch,
  } = useActivityReport(appliedParams);

  const {
    insight,
    isLoading: insightLoading,
    error: insightError,
    refetch: refetchInsight,
  } = useActivityReportInsight({
    customerId: appliedParams.customerId,
    addressId: appliedParams.addressId,
    dateFrom: appliedParams.startDate ? appliedParams.startDate.toISOString() : null,
    dateTo: appliedParams.endDate ? appliedParams.endDate.toISOString() : null,
    positiveOnly: isClientRole,
  });

  const handleSearch = useCallback(() => {
    if (!customer || !address || !startDate || !endDate) return;

    const start = startDate.toDate();
    start.setHours(0, 0, 0, 0);

    const end = endDate.toDate();
    end.setHours(23, 59, 59, 999);

    setAppliedParams({
      customerId: customer.Id,
      addressId: address.Id,
      startDate: start,
      endDate: end,
    });
  }, [customer, address, startDate, endDate]);

  useEffect(() => {
    if (addresses.length > 0 && !address) {
      setAddress(addresses[0]);
    } else if (addresses.length === 0) {
      setAddress(null);
    }
  }, [addresses]);

  useEffect(() => {
    setAddress(null);
  }, [customer?.Id]);

  const hasUnappliedChanges = useMemo(() => {
    if (!customer || !address) return false;
    return (
      customer.Id !== appliedParams.customerId ||
      address.Id !== appliedParams.addressId ||
      startDate?.format('YYYY-MM-DD') !== (appliedParams.startDate ? dayjs(appliedParams.startDate).format('YYYY-MM-DD') : null) ||
      endDate?.format('YYYY-MM-DD') !== (appliedParams.endDate ? dayjs(appliedParams.endDate).format('YYYY-MM-DD') : null)
    );
  }, [customer, address, startDate, endDate, appliedParams]);

  const handleToday = () => {
    setStartDate(dayjs());
    setEndDate(dayjs());
  };

  const handleLastWeek = () => {
    setStartDate(dayjs().subtract(1, 'week').startOf('week'));
    setEndDate(dayjs().subtract(1, 'week').endOf('week'));
  };

  const handleCurrentMonth = () => {
    setStartDate(dayjs().startOf('month'));
    setEndDate(dayjs().endOf('month'));
  };

  const handleLastMonth = () => {
    setStartDate(dayjs().subtract(1, 'month').startOf('month'));
    setEndDate(dayjs().subtract(1, 'month').endOf('month'));
  };

  const hasAppliedSearch = appliedParams.customerId && appliedParams.addressId;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Top Section: Filters + Duration Stats */}
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch' }}>
          <SearchFilters
            customers={customers}
            customer={customer}
            setCustomer={setCustomer}
            customersLoading={customersLoading}
            addresses={addresses}
            address={address}
            setAddress={setAddress}
            addressesLoading={addressesLoading}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            onSearch={handleSearch}
            onToday={handleToday}
            onLastWeek={handleLastWeek}
            onCurrentMonth={handleCurrentMonth}
            onLastMonth={handleLastMonth}
            reportLoading={reportLoading}
            hasUnappliedChanges={hasUnappliedChanges}
          />
          <DurationStatsPanel
            durationStats={durationStats}
            isLoading={reportLoading}
            hasData={!!appliedParams.customerId}
          />
        </Box>

        {/* Error Alert */}
        {reportError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {reportError}
          </Alert>
        )}

        {/* Prompt to select client and location or click search */}
        {!hasAppliedSearch ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {(!customer || !address) ? 'Please select a client and location' : 'Click Search to load report'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(!customer || !address)
                ? 'Choose a client and location from the dropdowns above to view the activity report.'
                : 'Select your date range and click the Search button to load the activity report.'}
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Stats Cards Section */}
            {reportLoading ? (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {[1, 2, 3].map((i) => (
                  <Grid item xs={12} md={4} key={i}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                      <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
                      <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <PatrolStatsCard
                    startDate={startDate?.format('MMM D, YYYY')}
                    endDate={endDate?.format('MMM D, YYYY')}
                    stats={patrolStats}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CheckpointStatsCard
                    startDate={startDate?.format('MMM D, YYYY')}
                    endDate={endDate?.format('MMM D, YYYY')}
                    stats={checkpointStats}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  {insightLoading ? (
                    <AIInsightSkeleton />
                  ) : insightError ? (
                    <AIInsightError error={insightError} onRetry={refetchInsight} />
                  ) : insight ? (
                    <AIInsightPanel insight={insight} />
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.paper',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 200,
                      }}
                    >
                      <AutoAwesomeIcon sx={{ color: 'text.disabled', fontSize: 40, mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        AI Insights loading...
                      </Typography>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Patrol Table */}
            {reportLoading ? (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  bgcolor: 'background.paper',
                }}
              >
                <Skeleton variant="rectangular" height={40} sx={{ mb: 2, borderRadius: 1 }} />
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
                ))}
              </Paper>
            ) : (
              <PatrolTable
                patrols={patrols}
                customerId={customer?.Id}
                isLoading={reportLoading}
                onRefetch={refetch}
                startDate={startDate?.format('MMM D, YYYY')}
                endDate={endDate?.format('MMM D, YYYY')}
                customerName={customer?.Name}
                locationName={address?.Name}
                patrolStats={patrolStats}
                readOnly={isClientRole}
              />
            )}
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ActivityReport;
